(ns ui.captions.editor
  "The transcript editor of the captions sidebar: the words of the
   captions clip as one ProseMirror paragraph, edited like plain text.

   Each word is a `word` mark that carries its uuid, times and emphasis.
   After every change `normalize` compares the marks with what
   `app.captions.transcript-edit/reconcile` makes of the text (typo fix,
   split, merge, new word) and repairs them, so the document always
   holds one mark per blank-separated token. The times travel in the
   marks, which is why undo and redo bring them back exactly. A word
   typed in this session is marked `fresh`: its time is a guess that is
   made again while the word grows, until the stored word gets other
   times (the time fields of the sidebar).

   A phrase break (Enter) is a `phrase_break` node, shown as a line
   break: the word after it gets `:word/break?`, where the caption
   styles start a new phrase. Backspace right after a break takes it
   away again.

   The word at the playhead is a decoration, a click moves the playhead
   to the word, Mod-b toggles the emphasis. The words go to the store
   as `[:captions/edit-words clip-uuid words]`; a change of the stored
   words from elsewhere (transcribe again, the time fields) comes back
   through `sync!`."
  (:require [app.captions.transcript-edit :as transcript-edit]
            ["prosemirror-commands" :as prosemirror-commands]
            ["prosemirror-history" :as prosemirror-history]
            ["prosemirror-keymap" :as prosemirror-keymap]
            ["prosemirror-model" :as prosemirror-model]
            ["prosemirror-state" :as prosemirror-state]
            ["prosemirror-view" :as prosemirror-view]))

(def editor-id
  "captions-transcript-editor")

(def css
  (str ".captions-transcript .ProseMirror{outline:none;white-space:pre-wrap;word-break:break-word;line-height:1.9;min-height:4rem;}"
       ".captions-transcript .ProseMirror p{margin:0;}"
       ".captions-transcript .ProseMirror br.captions-break{content:'';}"
       ".captions-word{border-radius:4px;padding:2px 1px;cursor:text;}"
       ".captions-word:hover{background-color:rgb(55 65 81);}"
       ".captions-word-emphasis{color:rgb(250 204 21);font-weight:600;}"
       ".captions-word-current,.captions-word-current:hover{background-color:rgb(79 70 229);}"))

(defn mark->dom
  [^js mark]
  (let [attrs (.-attrs mark)]
    (clj->js ["span"
              {:class (str "captions-word"
                           (when (unchecked-get attrs "emphasis")
                             " captions-word-emphasis"))
               :data-word-uuid (unchecked-get attrs "uuid")
               :data-start (unchecked-get attrs "start")
               :data-end (unchecked-get attrs "end")
               :data-emphasis (str (boolean (unchecked-get attrs "emphasis")))
               :data-fresh (str (boolean (unchecked-get attrs "fresh")))}
              0])))

(defn dom->attrs
  "Reads the mark back from a span the browser made itself (e.g. a
   clone of a word's span while typing)."
  [^js dom]
  #js {:uuid (.getAttribute dom "data-word-uuid")
       :start (js/parseInt (.getAttribute dom "data-start") 10)
       :end (js/parseInt (.getAttribute dom "data-end") 10)
       :emphasis (= "true" (.getAttribute dom "data-emphasis"))
       :fresh (= "true" (.getAttribute dom "data-fresh"))})

(def schema
  (prosemirror-model/Schema.
    (clj->js
      {:nodes {:doc {:content "paragraph"}
               :paragraph {:content "(text | phrase_break)*"
                           :toDOM (fn [_node]
                                    #js ["p" 0])
                           :parseDOM [{:tag "p"}]}
               :text {}
               :phrase_break {:inline true
                              :selectable false
                              :toDOM (fn [_node]
                                       #js ["br" #js {:class "captions-break"}])
                              :parseDOM [{:tag "br"}]}}
       :marks {:word {:attrs {:uuid {}
                              :start {}
                              :end {}
                              :emphasis {:default false}
                              :fresh {:default false}}
                      :toDOM mark->dom
                      :parseDOM [{:tag "span[data-word-uuid]"
                                  :getAttrs dom->attrs}]}}})))

(def word-type
  (.. schema -marks -word))

(def break-type
  (.. schema -nodes -phrase_break))

(defn break-node?
  [^js node]
  (and node
       (= break-type (.-type node))))

(defn word-mark
  [word]
  (.create word-type
           #js {:uuid (str (:word/uuid word))
                :start (:word/start word)
                :end (:word/end word)
                :emphasis (boolean (:word/emphasis? word))
                :fresh (boolean (:word/fresh? word))}))

(defn mark-word
  "The word map of a `word` mark."
  [^js mark]
  (let [attrs (.-attrs mark)]
    {:word/uuid (uuid (unchecked-get attrs "uuid"))
     :word/start (unchecked-get attrs "start")
     :word/end (unchecked-get attrs "end")
     :word/emphasis? (boolean (unchecked-get attrs "emphasis"))
     :word/fresh? (boolean (unchecked-get attrs "fresh"))}))

(defn node-word
  [^js node]
  (when node
    (some (fn [^js mark]
            (when (= word-type (.-type mark))
              (mark-word mark)))
          (.-marks node))))

(defn doc
  "The words as one paragraph, separated by blanks without a mark, or
   by a phrase break before a word that starts a phrase."
  [words]
  (let [nodes (->> words
                   (map-indexed (fn [index word]
                                  (cond-> [(.text schema
                                                  (:word/text word)
                                                  #js [(word-mark word)])]
                                    (pos? index)
                                    (conj (if (:word/break? word)
                                            (.create break-type)
                                            (.text schema " "))))))
                   (mapcat reverse))]
    (.node schema
           "doc"
           nil
           #js [(.node schema "paragraph" nil (into-array nodes))])))

(defn segments
  "The inline nodes of the paragraph: `{:text :from :word}` for a text
   node, `{:break? true :from}` for a phrase break, `:from` as document
   position."
  [^js doc]
  (let [out (volatile! [])]
    (.forEach (.-firstChild doc)
              (fn [^js node offset]
                (vswap! out conj (if (break-node? node)
                                   {:break? true
                                    :from (inc offset)}
                                   {:text (.-text node)
                                    :from (inc offset)
                                    :word (node-word node)}))))
    @out))

(defn blank?
  [ch]
  (some? (re-matches #"\s" ch)))

(defn add-char
  "A blank or a phrase break (`ch` = `::break`) ends the token; the
   break also marks the next token."
  [{:keys [current] :as acc} [pos ch word]]
  (cond
    (= ::break ch)
    (cond-> (assoc acc :current nil :break? true)
      current (update :tokens conj current))

    (blank? ch)
    (cond-> (assoc acc :current nil)
      current (update :tokens conj current))

    :else
    (let [token (or current
                    {:text ""
                     :from pos
                     :words []
                     :break? (boolean (:break? acc))})
          known? (= (:word/uuid word)
                    (:word/uuid (peek (:words token))))]
      (assoc acc
             :break? false
             :current
             (cond-> (-> token
                         (update :text str ch)
                         (assoc :to (inc pos)))
               (and word (not known?)) (update :words conj word))))))

(defn tokens
  "The blank-separated pieces of the text with their document range,
   the words they are made of and whether a phrase break stands before
   them, the input of `app.captions.transcript-edit/reconcile`."
  [segments]
  (let [chars (mapcat (fn [{:keys [text from word break?]}]
                        (if break?
                          [[from ::break nil]]
                          (map-indexed (fn [index ch]
                                         [(+ from index) ch word])
                                       text)))
                      segments)
        {:keys [tokens current]} (reduce add-char
                                         {:tokens []
                                          :current nil}
                                         chars)]
    (cond-> tokens
      current (conj current))))

(defn insert-break
  "A ProseMirror command: a phrase break at the cursor, in place of the
   blanks around it. Inside a word it splits the word like a blank
   does."
  [^js state dispatch]
  (let [selection (.-selection state)
        doc (.-doc state)
        from (.-from selection)
        to (.-to selection)
        blank-at? (fn [pos]
                    (let [node (.nodeAt doc pos)]
                      (and node
                           (.-isText node)
                           (= " " (.-text node)))))
        break-at? (fn [pos]
                    (break-node? (.nodeAt doc pos)))
        from (if (blank-at? (dec from)) (dec from) from)
        to (if (blank-at? to) (inc to) to)]
    ;; One break is enough, a second one would be an empty phrase:
    (when (and dispatch
               (not (break-at? (dec from)))
               (not (break-at? to)))
      (dispatch (.scrollIntoView (.replaceWith (.-tr state) from to (.create break-type)))))
    true))

(defn remove-break
  "A ProseMirror command for Backspace: the break right before the
   cursor becomes a blank again. Any other Backspace is left to the
   base keymap."
  [^js state dispatch]
  (let [position (.. state -selection -$from)]
    (when (and (.-empty (.-selection state))
               (break-node? (.-nodeBefore position)))
      (when dispatch
        (dispatch (.replaceWith (.-tr state)
                                (dec (.-pos position))
                                (.-pos position)
                                (.text schema " "))))
      true)))

(defn doc-words
  "The words of a document."
  [^js doc clip-range]
  (transcript-edit/reconcile (tokens (segments doc))
                             clip-range))

(defn run
  "A marked range, comparable between what is and what should be."
  [from to word]
  [from
   to
   (:word/uuid word)
   (:word/start word)
   (:word/end word)
   (boolean (:word/emphasis? word))
   (boolean (:word/fresh? word))])

(defn actual-runs
  [segments]
  (into #{}
        (keep (fn [{:keys [text from word]}]
                (when word
                  (run from (+ from (count text)) word))))
        segments))

(defn normalize
  "The transaction that gives every token exactly the mark of its
   word, or nil when the marks are right. A mark step does not move
   positions, so all steps use the positions of the current document."
  [^js state clip-range]
  (let [doc-segments (segments (.-doc state))
        doc-tokens (tokens doc-segments)
        words (transcript-edit/reconcile doc-tokens clip-range)
        by-run (into {}
                     (map (fn [token word]
                            [(run (:from token) (:to token) word) word])
                          doc-tokens
                          words))
        actual (actual-runs doc-segments)
        stale (remove by-run actual)
        missing (remove (comp actual key) by-run)]
    (when (or (seq stale)
              (seq missing))
      (let [tr (.-tr state)]
        (doseq [[from to] stale]
          (.removeMark tr from to word-type))
        (doseq [[[from to] word] missing]
          (.addMark tr from to (word-mark word)))
        tr))))

(defn normalize-plugin
  [context]
  (prosemirror-state/Plugin.
    #js {:appendTransaction
         (fn [transactions _old-state new-state]
           (when (some (fn [^js tr]
                         (.-docChanged tr))
                       transactions)
             (normalize new-state (:clip-range @context))))}))

(def playhead-key
  (prosemirror-state/PluginKey. "captionsPlayhead"))

(defn playhead-decorations
  [^js doc word-uuid]
  (let [decorations (keep (fn [{:keys [text from word]}]
                            (when (and word-uuid
                                       (= word-uuid (:word/uuid word)))
                              (.inline prosemirror-view/Decoration
                                       from
                                       (+ from (count text))
                                       #js {:class "captions-word-current"})))
                          (segments doc))]
    (.create prosemirror-view/DecorationSet
             doc
             (into-array decorations))))

(defn playhead-plugin
  "Highlights the word at the playhead, which arrives as the meta
   `{:uuid}` of a transaction (see `set-current!`)."
  []
  (prosemirror-state/Plugin.
    #js {:key playhead-key
         :state #js {:init (fn [_config _state]
                             {:uuid nil
                              :decorations (.-empty prosemirror-view/DecorationSet)})
                     :apply (fn [^js tr value _old-state ^js new-state]
                              (let [meta (.getMeta tr playhead-key)
                                    word-uuid (if meta
                                                (:uuid meta)
                                                (:uuid value))]
                                (if (or meta
                                        (.-docChanged tr))
                                  {:uuid word-uuid
                                   :decorations (playhead-decorations (.-doc new-state)
                                                                      word-uuid)}
                                  value)))}
         :props #js {:decorations (fn [state]
                                    (:decorations (.getState playhead-key state)))}}))

(defn cursor-word
  "The word the cursor is in (or right behind, or right before)."
  [^js state]
  (let [position (.. state -selection -$from)]
    (or (node-word (.-nodeBefore position))
        (node-word (.-nodeAfter position)))))

(defn selection-runs
  "The marked ranges inside the selection, or the one of the word at
   the cursor."
  [^js state]
  (let [selection (.-selection state)
        from (.-from selection)
        to (.-to selection)
        cursor-uuid (when (.-empty selection)
                      (:word/uuid (cursor-word state)))]
    (filter (fn [{:keys [text word] :as segment}]
              (when word
                (if (.-empty selection)
                  (= cursor-uuid (:word/uuid word))
                  (and (< (:from segment) to)
                       (< from (+ (:from segment) (count text)))))))
            (segments (.-doc state)))))

(defn toggle-emphasis
  "A ProseMirror command: emphasizes the words of the selection, or
   takes the emphasis away when all of them have it."
  [^js state dispatch]
  (let [runs (selection-runs state)
        emphasis? (not (every? (comp :word/emphasis? :word) runs))]
    (when (seq runs)
      (when dispatch
        (let [tr (.-tr state)]
          (doseq [{:keys [text from word]} runs]
            (.addMark tr
                      from
                      (+ from (count text))
                      (word-mark (assoc word :word/emphasis? emphasis?))))
          (dispatch tr)))
      true)))

(defn keymap-plugin
  []
  (prosemirror-keymap/keymap
    #js {"Mod-z" prosemirror-history/undo
         "Mod-Shift-z" prosemirror-history/redo
         "Mod-y" prosemirror-history/redo
         "Mod-b" toggle-emphasis
         "Enter" insert-break
         "Backspace" remove-break}))

(defn editor-state
  [words context]
  (.create prosemirror-state/EditorState
           #js {:doc (doc words)
                :plugins #js [(prosemirror-history/history)
                              (keymap-plugin)
                              (prosemirror-keymap/keymap prosemirror-commands/baseKeymap)
                              (normalize-plugin context)
                              (playhead-plugin)]}))

(defn dispatch-actions!
  [context actions]
  ((:dispatch! @context) {:actions actions}))

(defn emit-words!
  [context ^js state]
  (let [{:keys [clip-uuid clip-range]} @context
        words (doc-words (.-doc state) clip-range)]
    (swap! context assoc :words words)
    (dispatch-actions! context [[:captions/edit-words clip-uuid words]])))

(defn emit-selection!
  [context ^js state]
  (let [word-uuid (:word/uuid (cursor-word state))]
    (when (not= word-uuid (:selected @context))
      (swap! context assoc :selected word-uuid)
      (dispatch-actions! context
                         [[:store/assoc-in
                           [:editor2 :captions-sidebar :selected-word]
                           word-uuid]]))))

(defn paste!
  "Pasted text comes in as plain new words on the one line."
  [^js view ^js event]
  (let [text (some-> (.-clipboardData event)
                     (.getData "text/plain")
                     (.replace (js/RegExp. "\\s+" "g") " "))
        state (.-state view)]
    (when (seq text)
      (.dispatch view
                 (.replaceSelectionWith (.-tr state)
                                        (.text schema text)
                                        false)))
    true))

(defn click!
  [context ^js view pos]
  (let [doc (.. view -state -doc)
        word (or (node-word (.nodeAt doc pos))
                 (node-word (.-nodeBefore (.resolve doc pos))))]
    (when word
      (dispatch-actions! context [[:captions/seek-word (:word/uuid word)]]))
    false))

(defn create-view!
  [^js node context words]
  (let [view-ref (volatile! nil)
        view (prosemirror-view/EditorView.
               node
               #js {:state (editor-state words context)
                    :attributes #js {:spellcheck "false"}
                    :dispatchTransaction
                    (fn [^js tr]
                      (let [^js view @view-ref
                            state (.apply (.-state view) tr)]
                        (.updateState view state)
                        ;; The transactions of `sync!` and `set-current!`
                        ;; come from the store, nothing goes back:
                        (when-not (or (.getMeta tr "captions/sync")
                                      (.getMeta tr playhead-key))
                          (when (.-docChanged tr)
                            (emit-words! context state))
                          (emit-selection! context state))))
                    :handlePaste (fn [view event _slice]
                                   (paste! view event))
                    :handleDrop (fn [_view _event _slice _moved]
                                  true)
                    :handleClick (fn [view pos _event]
                                   (click! context view pos))})]
    (vreset! view-ref view)
    view))

(defn ^js view-of
  [^js node]
  (when node
    (unchecked-get node "captionsEditor")))

(defn same-text?
  "Whether the words differ only in what the marks hold (times,
   emphasis), not in the text or the breaks of the document."
  [words other-words]
  (= (map (juxt :word/uuid :word/text (comp boolean :word/break?)) words)
     (map (juxt :word/uuid :word/text (comp boolean :word/break?)) other-words)))

(defn sync!
  "Brings stored words that differ from the document into it: changed
   times or emphasis as mark updates (the cursor and the history stay),
   anything else as a fresh document."
  [^js view context words]
  (let [shown (:words @context)]
    (when (not= (map transcript-edit/comparable words)
                (map transcript-edit/comparable shown))
      (swap! context assoc :words (vec words))
      (if (same-text? words shown)
        (let [state (.-state view)
              tr (.-tr state)]
          (doseq [[token word] (map vector
                                    (tokens (segments (.-doc state)))
                                    words)]
            (.addMark tr (:from token) (:to token) (word-mark word)))
          (.dispatch view
                     (-> tr
                         (.setMeta "captions/sync" true)
                         (.setMeta "addToHistory" false))))
        (.updateState view (editor-state words context))))))

(defn current-word
  [words ms]
  (some (fn [word]
          (when (and (<= (:word/start word) ms)
                     (< ms (:word/end word)))
            word))
        words))

(defn set-current!
  "Moves the highlight to the word at the playhead and keeps it in
   view while the user is not typing."
  [^js view context ms]
  (let [word-uuid (:word/uuid (current-word (:words @context) ms))]
    (when (not= word-uuid (:current @context))
      (swap! context assoc :current word-uuid)
      (.dispatch view
                 (.setMeta (.. view -state -tr)
                           playhead-key
                           {:uuid word-uuid}))
      (when-not (.hasFocus view)
        (some-> (.querySelector (.-dom view) ".captions-word-current")
                (.scrollIntoView #js {:block "nearest"}))))))

(defn mount!
  [^js node {:keys [words] :as params}]
  (let [context (atom (-> (select-keys params [:clip-uuid :clip-range :dispatch!])
                          (assoc :words (vec words))))
        view (create-view! node context words)]
    (unchecked-set node "captionsEditor" view)
    (unchecked-set node "captionsContext" context)
    (set-current! view context (:playhead-ms params))))

(defn update!
  [^js node {:keys [words playhead-ms] :as params}]
  (when-let [^js view (view-of node)]
    (let [context (unchecked-get node "captionsContext")]
      (swap! context merge (select-keys params [:clip-range :dispatch!]))
      (sync! view context words)
      (set-current! view context playhead-ms))))

(defn unmount!
  [^js node]
  (when-let [^js view (view-of node)]
    (.destroy view)
    (unchecked-set node "captionsEditor" nil)
    (unchecked-set node "captionsContext" nil)))

(defn on-render
  [params]
  (fn [{:replicant/keys [node life-cycle]}]
    (case life-cycle
      :replicant.life-cycle/mount (mount! node params)
      :replicant.life-cycle/update (update! node params)
      :replicant.life-cycle/unmount (unmount! node)
      nil)))

(defn element
  "The editor of the `words` (the stored words of the captions clip
   with times relative to the media). Replicant only owns the empty
   host, ProseMirror renders into it.

   - `:clip-uuid` of the captions clip, part of the emitted action
   - `:clip-range` `{:lo :hi}`, the part of the media the clip shows
   - `:playhead-ms` the media time of the playhead
   - `:dispatch!` the `:replicant/dispatch!` of the store"
  [{:keys [clip-uuid] :as params}]
  [:div {:id editor-id
         :replicant/key (str editor-id "-" clip-uuid)
         :class ["captions-transcript" "rounded" "border" "border-gray-900"
                 "text-white" "text-sm" "px-2" "py-1"]
         :style {:background-color "oklch(0.21 0.034 264.665)"}
         :replicant/on-render (on-render params)}])

(defn toggle-emphasis!
  "For the button of the sidebar: runs the command on the editor
   mounted in `document` and gives it the focus back."
  [^js document]
  (when-let [^js view (view-of (.getElementById document editor-id))]
    (toggle-emphasis (.-state view)
                     (fn [tr]
                       (.dispatch view tr)))
    (.focus view)))
